/**
 * @file ItemSupplierField.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-02
 * @modified 2026-11-02
 * @description The Supplier field on Edit / Add Item (InventoryItemFormModal;
 *              Cam, 30 Oct / 2 Nov 2026). Controlled — the form holds the key
 *              ('a:<id>' / 'g:<parent id>' / '') and saves it AFTER the item
 *              itself, through PUT /api/inventory/:id/supplier, so the item's
 *              own save (and its Xero push) never carries it: the supplier is
 *              local only. useItemSupplierField loads what the item names now
 *              and says whether it changed. Greyed with the reason before
 *              migrations/item_supplier.sql or without 'Edit inventory items'.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config';
import ItemSupplierPicker from './ItemSupplierPicker';
import useItemSupplierOptions from '../hooks/useItemSupplierOptions';
import useAccess from '../hooks/useAccess';
import { NOT_READY_REASON } from '../utils/itemSupplier';
import './ItemSupplier.css';

/**
 * The field's state for an item window: { value, setValue, current, changed,
 * lists, block, save(itemId) → { ok, message?, error? } }.
 */
export function useItemSupplierField({ isOpen, itemId = null }) {
  const lists = useItemSupplierOptions({ enabled: !!isOpen });
  const { can } = useAccess();
  const [current, setCurrent] = useState(null);
  const [value, setValue] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    setValue('');
    setCurrent(null);
    setLoaded(!itemId);
    if (!itemId) return undefined;
    let cancelled = false;
    fetch(`${API_URL}/api/inventory/${itemId}/supplier`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        const s = d && d.ready !== false ? d.supplier : null;
        setCurrent(s);
        setValue(s?.key || '');
        setLoaded(true);
      })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [isOpen, itemId]);

  const block = lists.ready === false ? (lists.reason || NOT_READY_REASON)
    : !can('inventory.edit') ? "Changing an item's supplier needs 'Edit inventory items'"
      : (!loaded || !lists.loaded) ? 'Loading suppliers…' : null;
  const changed = loaded && (value || '') !== (current?.key || '');

  const save = useCallback(async (id) => {
    if (!changed || !id) return { ok: true };
    try {
      const res = await fetch(`${API_URL}/api/inventory/${id}/supplier`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ supplier: value || null })
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) return { ok: false, error: d.error || 'The supplier could not be saved' };
      setCurrent(d.supplier || null);
      setValue(d.supplier?.key || '');
      return { ok: true, message: d.message || null };
    } catch {
      return { ok: false, error: 'The supplier could not be saved' };
    }
  }, [changed, value]);

  return { value, setValue, current, changed, lists, block, save };
}

function ItemSupplierField({ field }) {
  const { value, setValue, current, lists, block } = field;
  return (
    <div className="item-details edit-form item-supplier-form">
      <div className="detail-row item-supplier">
        <div className="detail-group" title={block && block !== 'Loading suppliers…' ? block : undefined}>
          <label>Supplier</label>
          <ItemSupplierPicker value={value} onChange={setValue} lists={lists} current={current}
            disabled={!!block} placeholder={block === 'Loading suppliers…' ? block : 'Choose the supplier…'} />
          <p className="is-hint">
            {block && block !== 'Loading suppliers…' ? block
              : 'Who it is bought from — filled in when the item is picked on an order, invoice, PO or bill, and pinned at the top of the supplier list. A supplier group fills nothing: one of its depots is picked each time. Kept here only — never sent to Xero.'}
          </p>
        </div>
      </div>
    </div>
  );
}

export default ItemSupplierField;
